import {
    isRichTextEmpty,
    RichTextDoc,
    RichTextMark,
    RichTextNode,
    richTextNodes,
    toCanonicalPath,
} from '@longy/common/cms'
import { Fragment, ReactNode } from 'react'

import { cn } from '../../lib/utils'

/**
 * Only absolute http(s), mail and phone links, and site-relative paths survive.
 *
 * `javascript:`, `data:` and protocol-relative `//evil.com` are dropped, and the text then renders
 * unlinked. This and `safeColor` are the only two places an author-controlled value reaches an
 * attribute, so they are the entire injection surface of rendered prose.
 */
export const safeHref = (href: unknown): string | null => {
    if (typeof href !== 'string') return null
    const value = href.trim()
    if (/^https?:\/\//i.test(value) || /^mailto:/i.test(value) || /^tel:/i.test(value)) return value
    return /^\/(?!\/)/.test(value) ? toCanonicalPath(value) : null
}

/** Every value reaching an inline style is an injection boundary, so only exact hex passes. */
export const safeColor = (value: unknown): string | null =>
    typeof value === 'string' && /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value)
        ? value
        : null

const alignmentClass = (node: RichTextNode): string | undefined => {
    const align = node.attrs?.['textAlign']
    if (align === 'center') return 'text-center'
    if (align === 'right') return 'text-right'
    if (align === 'justify') return 'text-justify'
    return undefined
}

const isExternal = (href: string) => /^(https?:)?\/\//i.test(href)

const renderMarkedText = (node: RichTextNode, key: number): ReactNode => {
    let content: ReactNode = node.text ?? ''

    for (const mark of node.marks ?? []) {
        content = applyMark(mark, content)
    }

    return <Fragment key={key}>{content}</Fragment>
}

const applyMark = (mark: RichTextMark, content: ReactNode): ReactNode => {
    switch (mark.type) {
        case 'bold':
            return <strong>{content}</strong>
        case 'italic':
            return <em>{content}</em>
        case 'underline':
            return <u>{content}</u>
        case 'strike':
            return <s>{content}</s>
        case 'code':
            return <code>{content}</code>
        case 'textStyle': {
            // Font size is deliberately dropped: block copy is responsive, so an absolute size
            // would freeze it at one breakpoint's scale. A pasted document can carry marks the
            // editor itself cannot produce, so the renderer drops it rather than trusting the
            // editor's configuration.
            const color = safeColor(mark.attrs?.['color'])
            return color ? <span style={{ color }}>{content}</span> : <>{content}</>
        }
        case 'link': {
            const href = safeHref(mark.attrs?.['href'])
            if (!href) return <>{content}</>
            const external = isExternal(href)
            return (
                <a
                    href={href}
                    className="underline underline-offset-2"
                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                    {content}
                </a>
            )
        }
        default:
            // An unknown mark keeps its text and loses only the decoration.
            return <>{content}</>
    }
}

const renderNodes = (nodes: RichTextNode[], inline: boolean): ReactNode[] =>
    nodes.map((node, index) => renderNode(node, index, inline))

const renderNode = (node: RichTextNode, key: number, inline: boolean): ReactNode => {
    switch (node.type) {
        case 'text':
            return renderMarkedText(node, key)
        case 'hardBreak':
            return <br key={key} />
        case 'paragraph':
            // Inside a label a paragraph would be invalid html, so the phrasing-only variant keeps
            // the content and drops the wrapper.
            return inline ? (
                <Fragment key={key}>{renderNodes(node.content ?? [], inline)}</Fragment>
            ) : (
                <p key={key} className={alignmentClass(node)}>
                    {renderNodes(node.content ?? [], inline)}
                </p>
            )
        case 'heading': {
            const level = Number(node.attrs?.['level'] ?? 2)
            const Tag = (level >= 1 && level <= 6 ? `h${level}` : 'h2') as 'h2'
            return (
                <Tag key={key} className={alignmentClass(node)}>
                    {renderNodes(node.content ?? [], inline)}
                </Tag>
            )
        }
        case 'bulletList':
            return (
                <ul key={key} className="list-disc pl-5">
                    {renderNodes(node.content ?? [], inline)}
                </ul>
            )
        case 'orderedList':
            return (
                <ol key={key} className="list-decimal pl-5">
                    {renderNodes(node.content ?? [], inline)}
                </ol>
            )
        case 'listItem':
            return <li key={key}>{renderNodes(node.content ?? [], inline)}</li>
        case 'blockquote':
            return (
                <blockquote key={key} className="border-l-2 pl-4 italic">
                    {renderNodes(node.content ?? [], inline)}
                </blockquote>
            )
        case 'horizontalRule':
            return <hr key={key} className="my-6" />
        case 'image': {
            const src = safeHref(node.attrs?.['src'])
            if (!src) return null
            const width = node.attrs?.['width']
            const height = node.attrs?.['height']
            return (
                <img
                    key={key}
                    src={src}
                    alt={typeof node.attrs?.['alt'] === 'string' ? node.attrs['alt'] : ''}
                    width={typeof width === 'number' ? width : undefined}
                    height={typeof height === 'number' ? height : undefined}
                    loading="lazy"
                    decoding="async"
                    className="h-auto max-w-full"
                />
            )
        }
        default:
            // An unknown node keeps its content and loses only the wrapper.
            return <Fragment key={key}>{renderNodes(node.content ?? [], inline)}</Fragment>
    }
}

export type RichTextProps = {
    doc: RichTextDoc | null | undefined
    className?: string
}

/**
 * Walks the stored document and emits a closed set of tags. Nothing here ever reaches
 * `dangerouslySetInnerHTML`, which is the point of storing a tree rather than html.
 *
 * Typography belongs to the caller; this owns only paragraph and list spacing.
 */
export const RichText = ({ doc, className }: RichTextProps) => {
    if (isRichTextEmpty(doc)) return null

    return (
        <div className={cn('flex flex-col gap-4 [&_a]:font-medium', className)}>
            {renderNodes(richTextNodes(doc), false)}
        </div>
    )
}

/**
 * The phrasing-only sibling, for prose that sits inside a label or a button where a block element
 * would be invalid html. Both share the mark walk, so a mark taught to one is taught to both.
 */
export const InlineRichText = ({ doc, className }: RichTextProps) => {
    if (isRichTextEmpty(doc)) return null

    return <span className={className}>{renderNodes(richTextNodes(doc), true)}</span>
}
